import React from 'react';
import { Link } from 'react-router-dom';
import { brand } from '../mock';
import { DmcaReportForm } from '../components/DmcaReportForm';

const AGENT_NAME = '[Designated Agent Name — pending U.S. Copyright Office registration]';

const NOTICE_ITEMS = [
  'A physical or electronic signature of the copyright owner or a person authorized to act on their behalf.',
  'Identification of the copyrighted work claimed to have been infringed (e.g., the original listing photo or description).',
  'Identification of the material claimed to be infringing and information reasonably sufficient to let us locate it, such as the listing URL.',
  'Your contact information: name, postal address, telephone number, and email address.',
  'A statement that you have a good-faith belief that the use is not authorized by the copyright owner, its agent, or the law.',
  'A statement, under penalty of perjury, that the information in the notice is accurate and that you are the owner or authorized to act on the owner\u2019s behalf.',
];

const COUNTER_ITEMS = [
  'Your physical or electronic signature.',
  'Identification of the material removed and where it appeared before removal.',
  'A statement under penalty of perjury that you have a good-faith belief the material was removed as a result of mistake or misidentification.',
  'Your name, address, and telephone number, and a statement that you consent to the jurisdiction of the federal district court for your address (or, if outside the U.S., any judicial district in which we may be found), and that you will accept service of process from the person who provided the original notice.',
];

function Block({ title, children }) {
  return (
    <section className="mt-12">
      <h2 className="font-serif text-2xl md:text-3xl text-brand-green mb-4">{title}</h2>
      <div className="text-brand-green/80 leading-relaxed space-y-4">{children}</div>
    </section>
  );
}

function AgentCard() {
  return (
    <div data-testid="dmca-agent-card" className="mt-8 p-6 rounded-2xl bg-white border border-brand-cream-dark">
      <div className="text-xs uppercase tracking-[0.18em] text-brand-green/60">Designated DMCA Agent</div>
      <dl className="mt-4 grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-y-2 text-brand-green">
        <dt className="text-brand-green/60">Name</dt><dd data-testid="dmca-agent-name">{AGENT_NAME}</dd>
        <dt className="text-brand-green/60">Company</dt><dd>{brand.legal}</dd>
        <dt className="text-brand-green/60">Postal address</dt><dd data-testid="dmca-agent-address">{brand.address}</dd>
        <dt className="text-brand-green/60">Email</dt>
        <dd><a data-testid="dmca-agent-email" href={`mailto:${brand.email}?subject=DMCA%20Notice`} className="underline">{brand.email}</a></dd>
      </dl>
    </div>
  );
}

export default function Dmca() {
  return (
    <div className="bg-brand-cream" data-testid="dmca-page">
      <section className="bg-brand-green text-brand-cream py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
          <span className="text-xs uppercase tracking-[0.2em] text-brand-gold">Legal</span>
          <h1 className="mt-3 font-serif text-4xl md:text-5xl">DMCA &amp; Copyright Policy</h1>
          <p className="mt-4 text-brand-cream/70">Last updated: June 15, 2026</p>
        </div>
      </section>
      <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 py-14">
        <p className="text-brand-green/80 leading-relaxed">
          {brand.legal} respects the intellectual property of others. Realtors upload listing photos, descriptions and other
          media to the Service. If you believe content on {brand.legal}&apos;s website or apps infringes your copyright, send a
          notice to our designated agent under the Digital Millennium Copyright Act (17 U.S.C. &sect; 512).
        </p>
        <AgentCard />
        <Block title="How to submit a takedown notice">
          <p>Your written notice must include:</p>
          <ol className="list-decimal pl-6 space-y-2">{NOTICE_ITEMS.map((t) => <li key={t}>{t}</li>)}</ol>
          <p>We will remove or disable access to the material promptly and notify the user who posted it.</p>
          <p>Submit your notice online below, or email it to the agent above.</p>
          <DmcaReportForm />
        </Block>
        <Block title="Counter-notification">
          <p>If your content was removed and you believe it was a mistake, you may send a counter-notice to the agent above with:</p>
          <ol className="list-decimal pl-6 space-y-2">{COUNTER_ITEMS.map((t) => <li key={t}>{t}</li>)}</ol>
          <p>We may restore the material in 10&ndash;14 business days unless the complaining party notifies us it has filed a court action.</p>
        </Block>
        <Block title="Repeat infringers">
          <p>We terminate, in appropriate circumstances, the accounts of users who repeatedly infringe copyrights.</p>
        </Block>
        <Block title="Misrepresentations">
          <p>Under 17 U.S.C. &sect; 512(f), anyone who knowingly misrepresents that material is infringing, or was removed by mistake, may be liable for damages.</p>
        </Block>
        <p className="mt-12 text-sm text-brand-green/60">
          See also our <Link to="/terms" className="underline">Terms of Service</Link> and{' '}
          <Link to="/privacy" className="underline">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
}
